// src/components/form/phone-number-field.component.tsx
import React, { useMemo, useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomPhoneInput from '../react-native-material-wrapper/phone-input.component';
import { authStyles } from '../login/authStyles';
import { validatePhoneNumber } from '../../util/phone-validation';
import { ColorType } from '../../assets/colors/colors.g';

// Static defaults extracted to avoid recreating on each render
const DEFAULT_INPUT_STYLE = { height: 55, fontSize: 16 };
const DEFAULT_INPUT_PROPS = {};
const DROPDOWN_STYLE = { background: ColorType.GREY1 };

interface PhoneNumberFieldProps {
  value: string;
  onChange: (value: string) => void;

  // Validation options
  required?: boolean;

  /**
   * Default country code (ISO 3166-1 alpha-2, e.g., 'ca', 'us') used for:
   * - Phone number validation (helps parse numbers without '+' prefix)
   * - Display country in the phone input dropdown
   * Defaults to 'ca' if not specified.
   *
   * Note: The validation result (via onValidationChange) includes the detected
   * countryCode from the parsed phone number, which may differ from defaultCountry
   * once a valid number is entered.
   */
  defaultCountry?: string;

  // Callback to notify parent of validation state changes
  onValidationChange?: (
    isValid: boolean,
    normalized: string,
    validationResult: ReturnType<typeof validatePhoneNumber>,
  ) => void;

  // Layout & styling passthroughs
  className?: string;
  inputStyle?: React.CSSProperties;
  inputProps?: Record<string, any>;
  style?: React.CSSProperties;

  onFocus?: () => void;
  onBlur?: () => void;

  // Optional: show error immediately even if field hasn't been touched
  showErrorImmediately?: boolean;
}

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  required = false,
  defaultCountry = 'ca',
  onValidationChange,
  className,
  inputStyle = DEFAULT_INPUT_STYLE,
  inputProps = DEFAULT_INPUT_PROPS,
  style,
  onFocus,
  onBlur,
  showErrorImmediately = false,
}) => {
  const { t } = useTranslation();
  const classes = authStyles();
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  const validation = useMemo(
    () => validatePhoneNumber(value, { required, defaultCountry }),
    [value, required, defaultCountry],
  );

  // Notify parent of validation state changes
  useEffect(() => {
    onValidationChange?.(validation.isValid, validation.normalized, validation);
  }, [validation, onValidationChange]);

  // Use defaultCountry for display - keep it stable to avoid UI jumping during typing
  // The actual detected country is available via validation.countryCode in onValidationChange
  const displayCountry = defaultCountry || 'ca';

  // Show error if field has been interacted with (or showErrorImmediately),
  const showError = (hasBeenTouched || showErrorImmediately) && validation.errorKey !== null;
  const errorMessage = showError ? t(validation.errorKey!) : null;

  const handleFocus = () => {
    setHasBeenTouched(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setHasBeenTouched(true);
    onBlur?.();
  };

  const handleChange = (newValue: string) => {
    if (!hasBeenTouched && newValue.length > 0) {
      setHasBeenTouched(true);
    }
    onChange(newValue);
  };

  return (
    <Grid item xs={12} className={className || classes.phoneInput}>
      <CustomPhoneInput
        style={style}
        inputProps={{ required, ...inputProps }}
        country={displayCountry}
        inputStyle={inputStyle}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={t('phone_number_placeholder')}
        dropdownStyle={DROPDOWN_STYLE}
        countryCodeEditable={false}
      />
      {errorMessage && (
        <Typography className={classes.invalidPhoneMessage}>{errorMessage}</Typography>
      )}
    </Grid>
  );
};
